// İSG uzmanı portalı — Carbon UI Shell header search (S34-5, api-contract §34.3). Collapsed
// bir ikon düğmesi yerinde genişleyip bir metin girişine dönüşür (Carbon "header search"
// deseni — README/plan `S34-carbon-phase2.md` §S34-5); sonuçlar `Çalışma İzinleri` /
// `Taşeron/Yüklenici` / `Personel` gruplu bir popover'da listelenir. `AppHeader.tsx` yalnız
// `permits/list` erişimi VARSA render eder (uç zaten bu kapıya tabidir — grantsız principal
// zaten 403 görürdü, kontrol yalnız gereksiz bir isteği ÖNLER).
//
// **Combobox a11y (WAI-ARIA APG "Editable Combobox With List Autocomplete").** Giriş
// `role="combobox"` + `aria-expanded`/`aria-controls`/`aria-activedescendant` taşır; sonuçlar
// `role="listbox"`, her grup `role="group"` (`aria-label` ile), her satır `role="option"`.
// Sanal odak (`aria-activedescendant`) ÜÇ grubu TEK düz listede gezer (ArrowUp/Down) — grup
// başlıkları kendisi bir seçenek DEĞİL. Enter etkin seçeneği açar; Esc kapatır VE odağı
// tetikleyici düğmeye GERİ VERİR (bileşen kapanınca düğme yeniden mount olur, `triggerRef`
// bir `useEffect`te odaklanır — ilk mount'ta TETİKLENMEMESİ için `mountedRef` koruması).
// Dışarı tıklama (`pointerdown`, document) da kapatır — odağı GERİ VERMEZ (kullanıcı zaten
// başka bir yere tıkladı, dosya başlığındaki Esc/dış-tık ayrımı `ConfirmDialog.tsx`/`Modal.tsx`
// ile AYNI karar).
//
// **S36 #9 canlı QA — "ilk mount'ta atlanır" bayrağı StrictMode'un dev çifte-mount
// simülasyonuyla YENİLİYORDU.** Eski `mountedRef` deseni ("false ise true'ya çek ve ÇIK") yalnız
// BİRİNCİ efekt çağrısında doğru çalışıyordu; React 18+ `<StrictMode>` (`main.tsx`) dev'de her
// mount effect'ini bilerek İKİ KEZ çalıştırır (mount→cleanup→mount) — AYNI `useRef` ikisi
// arasında SIFIRLANMAZ, yani İKİNCİ çağrı bayrağı zaten `true` bulup erken-dönüşü ATLAR ve
// `open=false` iken (henüz hiç açılmamışken) tetikleyiciye odaklanırdı. Sonuç: sayfa YÜKLENİR
// YÜKLENMEZ arama düğmesi odaklanmış görünüyordu — `grep autoFocus` bunu asla YAKALAMAZ (çağrı
// `.focus()`, JSX prop'u DEĞİL). Düzeltme: "ilk mi" yerine `open`un ÖNCEKİ DEĞERİNİ tutan bir ref
// — yalnız GERÇEK açık→kapalı GEÇİŞİNDE odaklanır; StrictMode'un iki mount çağrısı da `open`u
// `false` bulduğundan (geçiş YOK) artık sahte bir odaklanma tetiklemez.
//
// Klavye kısayolu "/" — herhangi bir düzenlenebilir alanda ODAK YOKKEN input'u açar/odaklar
// (`isEditableTarget` koruması — aksi hâlde bir metin alanına "/" yazan kullanıcı arama
// kutusuna kaçırılırdı).
//
// **Vurgulama** (`splitHighlight`) — yalnız GÜVENLİ dize bölme (indexOf + slice); asla
// `dangerouslySetInnerHTML` ya da kullanıcı girdisinden bir regex İNŞA EDİLMEZ. Backend'in tam
// Türkçe harf katlamalı eşleşmesini (§34.5) BİREBİR yansıtmaz — yalnız `toLocaleLowerCase('tr')`
// ile bulunabilen alt-dizeyi vurgular; bulunamazsa (katlama farkı) düz metne DÜŞER, asla yanlış
// bir aralığı vurgulamaz.
//
// **Rota.** İzin → `/permits/:uuid`; taşeron → `/contractors/:uuid`; personel → aynı taşeron
// detayının Personel sekmesi (kişi bazlı bir rota YOK, api-contract §34.3 yalnız
// `SupplierUser.Uuid` + `contractorUuid` döner — `ContractorDetailPage.tsx`'e `location.state.
// openTab` ile taşınır, bkz. o dosyanın başlığı).

import {
  EmptyState,
  type SearchContractorHitDto,
  type SearchPermitHitDto,
  type SearchPersonnelHitDto,
  Spinner,
  Tag,
  cn,
  openPersonnelState,
  resolveApiErrorMessage,
  searchApi,
  useAuthStore,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { searchKeys } from '../lib/searchKeys';
import { contractorStatusTone } from './contractors/tone';
import { getPermitStatusPresentation } from './permits/permitEnums';

const MIN_QUERY_LENGTH = 2;
const MAX_QUERY_LENGTH = 100;
const DEBOUNCE_MS = 250;
const RESULT_LIMIT = 5;

type FlatOption =
  | { kind: 'permit'; id: string; hit: SearchPermitHitDto }
  | { kind: 'contractor'; id: string; hit: SearchContractorHitDto }
  | { kind: 'personnel'; id: string; hit: SearchPersonnelHitDto };

/** Odaklanılabilir/düzenlenebilir bir hedefte mi — "/" kısayolu yalnız BUNUN DIŞINDA açar. */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/** Güvenli alt-dize bölme (dosya başlığı) — bulunamazsa `null` (çağıran düz metne düşer). */
function splitHighlight(
  text: string,
  query: string,
): { pre: string; match: string; post: string } | null {
  if (!query) return null;
  const idx = text.toLocaleLowerCase('tr').indexOf(query.toLocaleLowerCase('tr'));
  if (idx === -1) return null;
  return {
    pre: text.slice(0, idx),
    match: text.slice(idx, idx + query.length),
    post: text.slice(idx + query.length),
  };
}

/** Eşleşen alt-diziyi kalın gösterir — renk DEĞİL (mavi bu derlemede "etkileşimli" anlamına
 * gelir, dosya başlığındaki `theme-carbon.css` notu; vurgu bir renk YÜKLEMEZ). */
function Highlighted({ text, query }: { text: string; query: string }) {
  const parts = splitHighlight(text, query);
  if (!parts) return <>{text}</>;
  return (
    <>
      {parts.pre}
      <span className="font-semibold">{parts.match}</span>
      {parts.post}
    </>
  );
}

function optionClasses(active: boolean) {
  return cn(
    'flex cursor-pointer flex-col gap-0.5 px-4 py-2',
    active ? 'bg-surface-muted' : 'hover:bg-surface-muted',
  );
}

export function HeaderSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Sorgu anahtarının PARÇASI (security review, `searchKeys.ts`in dosya başlığı) — görünürlük
  // principal'a göre değişir (§30.2), `principalUuid` yoksa bir refresh-hatası sonrası AYNI
  // sekmede oturum açan sonraki kullanıcı önceki kullanıcının önbelleğini AĞ İSTEĞİ OLMADAN
  // bulabilirdi (`AppHeader.tsx`in `onLogout`unun ele aldığı AYNI sınıf bulgu).
  const principalUuid = useAuthStore((s) => s.principal?.uuid);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // `open`un ÖNCEKİ değeri — yalnız GERÇEK açık→kapalı geçişinde tetikleyiciye odaklanmak için
  // (S36 #9 dosya başlığı notu; StrictMode'un çifte-mount'una dayanıklı).
  const prevOpenRef = useRef(open);

  // Debounce (dosya başlığı, ~250ms) — her tuş vuruşunda değil, yazım durduktan sonra.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  const trimmed = debouncedQuery.trim();
  const canSearch = trimmed.length >= MIN_QUERY_LENGTH;

  const params = { q: trimmed, limit: RESULT_LIMIT };
  const searchQuery = useQuery({
    queryKey: searchKeys.query(principalUuid, params),
    // `signal` — bir tuş vuruşu bir önceki isteği SUPERSEDE ederse TanStack aboneliği iptal
    // eder; `search()`e geçmek isteği tarayıcı seviyesinde de iptal eder (security review,
    // `client.ts`in dosya başlığı notu).
    queryFn: ({ signal }) => searchApi.search(params, { signal }),
    enabled: open && canSearch,
    staleTime: 30_000,
  });

  const data = searchQuery.data;
  const permitsGroup = data?.permits ?? null;
  const contractorsGroup = data?.contractors ?? null;
  const personnelGroup = data?.personnel ?? null;
  const isLoading = open && canSearch && searchQuery.fetchStatus === 'fetching';
  const isError = open && canSearch && searchQuery.status === 'error';

  const flatOptions = useMemo<FlatOption[]>(() => {
    const out: FlatOption[] = [];
    for (const hit of permitsGroup?.items ?? []) {
      out.push({ kind: 'permit', id: `header-search-permit-${hit.uuid}`, hit });
    }
    for (const hit of contractorsGroup?.items ?? []) {
      out.push({ kind: 'contractor', id: `header-search-contractor-${hit.uuid}`, hit });
    }
    for (const hit of personnelGroup?.items ?? []) {
      out.push({ kind: 'personnel', id: `header-search-personnel-${hit.supplierUserUuid}`, hit });
    }
    return out;
  }, [permitsGroup, contractorsGroup, personnelGroup]);

  // `id → düz liste indeksi` — her satırın kendi index'ini `findIndex` ile TEKRAR TEKRAR
  // ARAMASI yerine (dosya başlığı, N küçük olsa da gereksiz), tek geçişte kurulur.
  const indexById = useMemo(() => {
    const map = new Map<string, number>();
    flatOptions.forEach((option, index) => map.set(option.id, index));
    return map;
  }, [flatOptions]);

  // Yeni sonuçlar gelince ilk seçeneği CANLI vurgular (Enter'ın hemen bir sonucu açması için) —
  // sonuç yoksa vurgu yok.
  useEffect(() => {
    setActiveIndex(flatOptions.length > 0 ? 0 : -1);
  }, [flatOptions]);

  const overallEmpty =
    canSearch &&
    searchQuery.status === 'success' &&
    (permitsGroup?.items.length ?? 0) === 0 &&
    (contractorsGroup?.items.length ?? 0) === 0 &&
    (personnelGroup?.items.length ?? 0) === 0;

  function resetQuery() {
    setQuery('');
    setDebouncedQuery('');
    setActiveIndex(-1);
  }

  function openSearch() {
    setOpen(true);
  }

  function closeSearch() {
    setOpen(false);
    resetQuery();
  }

  // Kapanınca odağı tetikleyiciye GERİ VER — yalnız GERÇEK bir açık→kapalı geçişinde (dosya
  // başlığı notu, S36 #9).
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      triggerRef.current?.focus();
    }
    prevOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  // Dışarı tıklama — kapatır, odağı GERİ VERMEZ (dosya başlığı). Yalnız `open`/state
  // setter'ları (React'te KARARLI kimlik) kullanır — `resetQuery` gibi her render'da yeniden
  // kurulan bir yerel fonksiyona bilerek BAĞLI DEĞİL, bağımlılık listesi zaten TAM.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
        setDebouncedQuery('');
        setActiveIndex(-1);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // "/" kısayolu — herhangi bir düzenlenebilir alanda odak YOKKEN açar/odaklar. Bağımlılık
  // YOK (`[]`): `open`u OKUMAZ, her zaman açar + odaklar (zaten açıksa `setOpen(true)` no-op,
  // `inputRef.current?.focus()` yeniden odaklar — kapalıysa mount sonrası odaklama AYRI
  // `useEffect`in (yukarıda, `[open]`) işidir).
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isEditableTarget(document.activeElement)) return;
      event.preventDefault();
      setOpen(true);
      inputRef.current?.focus();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  function activate(option: FlatOption) {
    if (option.kind === 'permit') {
      navigate(`/permits/${option.hit.uuid}`);
    } else if (option.kind === 'contractor') {
      navigate(`/contractors/${option.hit.uuid}`);
    } else {
      // Kişi bazlı bir rota YOK (dosya başlığı) — taşeron detayının Personel sekmesi açılır ve
      // kişinin çekmecesi router state'iyle açılır (GL5).
      navigate(`/contractors/${option.hit.contractorUuid}`, {
        state: { openTab: 'personnel', ...openPersonnelState(option.hit.supplierUserUuid) },
      });
    }
    closeSearch();
  }

  function onInputKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatOptions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = flatOptions[activeIndex];
      if (option) activate(option);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closeSearch();
    }
  }

  const listboxId = 'header-search-listbox';
  const activeOption = flatOptions[activeIndex];
  const resultsVisible = open && trimmed.length > 0;

  // Ekran okuyucu canlı bölgesi (depo a11y kuralı — "async sonuçlar navigasyonsuz bir canlı
  // bölge gerektirir"). Görsel popover'dan AYRI, `sr-only` — görsel durum zaten popover'da.
  const liveStatus = !canSearch
    ? ''
    : isLoading
      ? t('search.loading')
      : isError
        ? resolveApiErrorMessage(searchQuery.error, t)
        : overallEmpty
          ? t('search.noResults')
          : t('search.status.results', { count: flatOptions.length });

  return (
    <div
      ref={containerRef}
      className="relative flex items-center"
      // Tab ile DIŞARI çıkma da kapatır (yalnız fare/dokunuşu yakalayan `pointerdown` dış-tık
      // dinleyicisinin KAÇIRDIĞI yol) — odak gerçekten bu kapsayıcının DIŞINA taşınıyorsa
      // (`relatedTarget` kapsayıcı İÇİNDE değilse) arama açık TAKILI kalmaz.
      onBlur={(event) => {
        if (containerRef.current && !containerRef.current.contains(event.relatedTarget as Node)) {
          closeSearch();
        }
      }}
    >
      {!open ? (
        <button
          ref={triggerRef}
          type="button"
          title={t('search.shortcutHint')}
          aria-label={t('search.trigger')}
          onClick={openSearch}
          className="flex h-12 w-12 items-center justify-center text-shell-fg-muted hover:bg-shell-hover hover:text-shell-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-shell-fg"
        >
          <Search className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : (
        <div className="relative flex h-12 w-80 items-center bg-shell-hover">
          <Search
            className="pointer-events-none absolute left-4 h-4 w-4 text-shell-fg-muted"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={resultsVisible}
            aria-controls={listboxId}
            aria-activedescendant={activeOption?.id}
            aria-autocomplete="list"
            aria-label={t('search.label')}
            maxLength={MAX_QUERY_LENGTH}
            placeholder={t('search.placeholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
            className="h-full w-full bg-transparent pl-11 pr-11 text-sm text-shell-fg placeholder:text-shell-fg-muted focus-visible:outline-none"
          />
          <button
            type="button"
            aria-label={t('search.close')}
            onClick={closeSearch}
            className="absolute right-0 flex h-12 w-11 items-center justify-center text-shell-fg-muted hover:text-shell-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-shell-fg"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* `<output>` — implicit ARIA role zaten "status" (`Spinner.tsx`in AYNI deseni); `role="status"`
          İKİNCİ bir kez YAZILMAZ. */}
      <output aria-live="polite" className="sr-only">
        {liveStatus}
      </output>

      {resultsVisible ? (
        // Combobox listbox popover'ı (dosya başlığı) — GERÇEK odak BİLEREK YOK (sanal odak
        // girişte); `<select>` (Biome'un önerisi) serbest bir popover'da KULLANILAMAZ.
        // biome-ignore lint/a11y/useFocusableInteractive: sanal odak (aria-activedescendant, girişte) kasıtlı, yukarıdaki not
        <div
          id={listboxId}
          // biome-ignore lint/a11y/useSemanticElements: role="listbox" kasıtlı, yukarıdaki not
          role="listbox"
          aria-label={t('search.label')}
          className="absolute right-0 top-full z-[var(--z-dropdown)] mt-1 max-h-[28rem] w-96 overflow-y-auto rounded-md border border-border bg-surface py-1 shadow-md"
        >
          {!canSearch ? (
            <p className="px-4 py-3 text-sm text-muted">{t('search.minChars')}</p>
          ) : isLoading && flatOptions.length === 0 ? (
            <div className="flex items-center justify-center px-4 py-6">
              <Spinner label={t('search.loading')} />
            </div>
          ) : isError ? (
            <p className="px-4 py-3 text-sm text-danger">
              {resolveApiErrorMessage(searchQuery.error, t)}
            </p>
          ) : overallEmpty ? (
            // `role="status"` BURADA yok (dosya başlığındaki tek `sr-only` canlı bölge zaten
            // aynı anı duyurur) — ikisi birden aynı metni İKİ KEZ okuturdu.
            <EmptyState
              variant="panel"
              title={t('search.noResults')}
              description={t('search.noResults.body')}
              className="px-4 py-4"
            />
          ) : (
            <>
              {/* S34-45 design review S4 — sıra SABİT kalır, yalnız BOŞ gruplar (bu dalda
                  `overallEmpty` ZATEN ELENDİĞİ için hepsi birden boş OLAMAZ) gizlenir; "Bu
                  grupta sonuç yok." metni gerçek isabetleri AŞAĞI itiyordu. */}
              {permitsGroup && permitsGroup.items.length > 0 ? (
                <SearchGroup label={t('search.group.permits')}>
                  {permitsGroup.items.map((hit) => {
                    const id = `header-search-permit-${hit.uuid}`;
                    const index = indexById.get(id) ?? -1;
                    const presentation = getPermitStatusPresentation(hit.status);
                    return (
                      <SearchOption
                        key={id}
                        id={id}
                        active={index === activeIndex}
                        onHover={() => setActiveIndex(index)}
                        onActivate={() => activate({ kind: 'permit', id, hit })}
                      >
                        <p className="truncate text-sm text-fg">
                          <Highlighted text={hit.title} query={trimmed} />
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted">
                          <Tag color={presentation.tagColor}>{t(presentation.labelKey)}</Tag>
                          <span className="truncate">{hit.contractorName}</span>
                        </div>
                      </SearchOption>
                    );
                  })}
                  {permitsGroup.hasMore ? <MoreHint t={t} /> : null}
                </SearchGroup>
              ) : null}

              {contractorsGroup && contractorsGroup.items.length > 0 ? (
                <SearchGroup label={t('search.group.contractors')}>
                  {contractorsGroup.items.map((hit) => {
                    const id = `header-search-contractor-${hit.uuid}`;
                    const index = indexById.get(id) ?? -1;
                    return (
                      <SearchOption
                        key={id}
                        id={id}
                        active={index === activeIndex}
                        onHover={() => setActiveIndex(index)}
                        onActivate={() => activate({ kind: 'contractor', id, hit })}
                      >
                        <p className="truncate text-sm text-fg">
                          <Highlighted text={hit.name} query={trimmed} />
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted">
                          <Tag color={contractorStatusTone(hit.status)}>
                            {t(`contractors.status.${hit.status.toLowerCase()}`)}
                          </Tag>
                          {hit.taxNumber ? (
                            <span className="truncate">
                              {t('contractors.form.field.taxNumber')}:{' '}
                              <Highlighted text={hit.taxNumber} query={trimmed} />
                            </span>
                          ) : null}
                        </div>
                      </SearchOption>
                    );
                  })}
                  {contractorsGroup.hasMore ? <MoreHint t={t} /> : null}
                </SearchGroup>
              ) : null}

              {personnelGroup && personnelGroup.items.length > 0 ? (
                <SearchGroup label={t('search.group.personnel')}>
                  {personnelGroup.items.map((hit) => {
                    const id = `header-search-personnel-${hit.supplierUserUuid}`;
                    const index = indexById.get(id) ?? -1;
                    const fullName = `${hit.firstName} ${hit.lastName}`.trim();
                    return (
                      <SearchOption
                        key={id}
                        id={id}
                        active={index === activeIndex}
                        onHover={() => setActiveIndex(index)}
                        onActivate={() => activate({ kind: 'personnel', id, hit })}
                      >
                        <p className="truncate text-sm text-fg">
                          <Highlighted text={fullName} query={trimmed} />
                        </p>
                        <p className="truncate text-xs text-muted">{hit.contractorName}</p>
                      </SearchOption>
                    );
                  })}
                  {personnelGroup.hasMore ? <MoreHint t={t} /> : null}
                </SearchGroup>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Tek grup — `role="group"` + görünür (aria-hidden) başlık, dosya başlığındaki gruplama notu.
 * `<fieldset>` (Biome'un önerisi) form denetimleri (radio/checkbox) içindir — `FilterChipGroup.tsx`
 * ile AYNI gerekçe, buradaki `role="group"` bir listbox İÇİ gruplamadır, form değil. */
function SearchGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: role="group" kasıtlı, yukarıdaki not
    <div role="group" aria-label={label}>
      <p aria-hidden="true" className="px-4 pt-2 pb-1 text-xs font-semibold text-muted">
        {label}
      </p>
      {children}
    </div>
  );
}

/**
 * Tek listbox seçeneği — dosya başlığındaki "Combobox a11y" notu: GERÇEK odak BİLEREK YOK.
 * Sanal odak (`aria-activedescendant`, kapsayan `<input role="combobox">`de) WAI-ARIA APG'nin
 * KENDİ deseni — `tabIndex`/`onKeyDown` eklemek seçeneği gerçekten odaklanabilir yapardı, bu da
 * combobox'ın "TEK gerçek odak noktası giriştir" kuralını ihlal ederdi (klavye yolu zaten
 * `onInputKeyDown`'da, girişin kendisinde). `<option>` (Biome'un önerisi) yalnız `<select>`/
 * `<datalist>` İÇİNDE geçerlidir, serbest bir popover'da KULLANILAMAZ.
 */
function SearchOption({
  id,
  active,
  onHover,
  onActivate,
  children,
}: {
  id: string;
  active: boolean;
  onHover: () => void;
  onActivate: () => void;
  children: ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/useFocusableInteractive: sanal odak (aria-activedescendant) kasıtlı, yukarıdaki not
    // biome-ignore lint/a11y/useKeyWithClickEvents: klavye yolu kapsayan combobox girişinin onKeyDown'ında, yukarıdaki not
    <div
      id={id}
      // biome-ignore lint/a11y/useSemanticElements: role="option" kasıtlı, yukarıdaki not
      role="option"
      aria-selected={active}
      onMouseEnter={onHover}
      onClick={onActivate}
      className={optionClasses(active)}
    >
      {children}
    </div>
  );
}

function MoreHint({ t }: { t: (key: string) => string }) {
  return <p className="px-4 pb-2 pt-1 text-xs text-muted">{t('search.group.hasMore')}</p>;
}
